import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { circle, distance, point } from '@turf/turf';

import { dragPan } from './modes.helpers';

import type { DrawModeContext } from './types';

type DragCircleModeType = {
  onClick: (state: any, e: any) => void;
  onDrag: (state: any, e: any) => void;
  onKeyDown?: (state: any, e: any) => void;
  onKeyUp?: (state: any, e: any) => void;
  onMapClick?: (state: any, e: any) => void;
  onMouseDown: (state: any, e: any) => void;
  onMouseMove: (state: any, e: any) => void;
  onMouseOut?: (state: any, e: any) => void;
  onMouseUp: (state: any, e: any) => void;
  onSetup: (opts: any) => any;
  onStop?: (state: any) => void;
  onTap: (state: any, e: any) => void;
  onTouchEnd: (state: any, e: any) => void;
  onTouchMove?: (state: any, e: any) => void;
  onTouchStart: (state: any, e: any) => void;
  onTrash?: (state: any) => void;
  toDisplayFeatures: (state: any, geojson: any, display: (feature: any) => void) => void;
};

const DragCircleMode: DragCircleModeType = {
  onClick: () => {
    // do nothing
  },
  onDrag: () => {
    // do nothing
  },
  onMouseDown: () => {
    // do nothing
  },
  onMouseMove: () => {
    // do nothing
  },
  onMouseUp: () => {
    // do nothing
  },
  onSetup: () => {
    // do nothing
  },
  onTap: () => {
    // do nothing
  },
  onTouchEnd: () => {
    // do nothing
  },
  onTouchStart: () => {
    // do nothing
  },
  toDisplayFeatures: () => {
    // do nothing
  }
};

Object.assign(DragCircleMode, MapboxDraw.modes.draw_polygon);

DragCircleMode.onClick = function (state: any) {
  state.polygon.properties.center = [];
};

DragCircleMode.onDrag = function (state: any, e: any) {
  const { center } = state.polygon.properties;
  if (center.length > 0) {
    const distanceInKm = distance(point(center), point([e.lngLat.lng, e.lngLat.lat]), { units: 'kilometers' });
    const circleFeature = circle(center, distanceInKm);
    if (circleFeature.geometry) {
      state.polygon.incomingCoords(circleFeature.geometry.coordinates);
    }
    state.polygon.properties.radiusInKm = distanceInKm;
  }
};

DragCircleMode.onMouseDown = function (state: any, e: any) {
  const { center: currentCenter } = state.polygon.properties;
  if (currentCenter.length === 0) {
    state.polygon.properties.center = [e.lngLat.lng, e.lngLat.lat];
  }
};

DragCircleMode.onMouseMove = function (state: any, e: any) {
  const { center } = state.polygon.properties;
  if (center.length > 0) {
    const distanceInKm = distance(point(center), point([e.lngLat.lng, e.lngLat.lat]), { units: 'kilometers' });
    const circleFeature = circle(center, distanceInKm);
    if (circleFeature.geometry) {
      state.polygon.incomingCoords(circleFeature.geometry.coordinates);
    }
    state.polygon.properties.radiusInKm = distanceInKm;
  }
};

DragCircleMode.onMouseUp = function (state: any) {
  const ctx = this as unknown as DrawModeContext;
  dragPan.enable(ctx);
  return ctx.changeMode(MapboxDraw.constants.modes.SIMPLE_SELECT, { featureIds: [state.polygon.id] });
};

DragCircleMode.onSetup = function () {
  const ctx = this as unknown as DrawModeContext;
  const polygon = ctx.newFeature({
    geometry: {
      coordinates: [[]],
      type: MapboxDraw.constants.geojsonTypes.POLYGON
    },
    properties: {
      center: [],
      isCircle: true
    },
    type: MapboxDraw.constants.geojsonTypes.FEATURE
  });

  ctx.addFeature(polygon);

  ctx.clearSelectedFeatures();
  MapboxDraw.lib.doubleClickZoom.disable(ctx);
  dragPan.disable(ctx);
  ctx.updateUIClasses({ mouse: MapboxDraw.constants.cursors.ADD });
  ctx.activateUIButton(MapboxDraw.constants.types.POLYGON);
  ctx.setActionableState({
    trash: true
  });

  return {
    currentVertexPosition: 0,
    polygon
  };
};

DragCircleMode.onTap = function (state: any) {
  state.polygon.properties.center = [];
};

DragCircleMode.onTouchEnd = function (state: any) {
  const ctx = this as unknown as DrawModeContext;
  dragPan.enable(ctx);
  return ctx.changeMode(MapboxDraw.constants.modes.SIMPLE_SELECT, { featureIds: [state.polygon.id] });
};

DragCircleMode.onTouchStart = function (state: any, e: any) {
  const currentCenter = state.polygon.properties.center;
  if (currentCenter.length === 0) {
    state.polygon.properties.center = [e.lngLat.lng, e.lngLat.lat];
  }
};

DragCircleMode.toDisplayFeatures = function (state: any, geojson: any, display: (feature: any) => void) {
  const isActivePolygon = geojson.properties.id === state.polygon.id;
  geojson.properties.active = isActivePolygon
    ? MapboxDraw.constants.activeStates.ACTIVE
    : MapboxDraw.constants.activeStates.INACTIVE;
  return display(geojson);
};

export { DragCircleMode };
